import * as d3 from "d3";

type Dimensions = {
  width: number;
  height: number;
  marginLeft: number;
  marginRight: number;
  marginTop: number;
  marginBottom: number;
  padding: number;
};

type ScaleConfig = {
  type: string;
  position: "left" | "right" | "top" | "bottom";
  domain?: (string | number | Date)[];
  range?: string[];
  strokeRange?: string[];
  extraSpace?: number;
  zeroAsMinValue?: boolean;
  dataMapping?: string;
  [key: string]: unknown;
};

export const positionRange = (
  position: "left" | "right" | "top" | "bottom",
  dimensions: {
    width: number;
    height: number;
    marginLeft: number;
    marginRight: number;
    marginTop: number;
    marginBottom: number;
    padding: number;
  },
) => {
  if (position === "left") {
    return [dimensions.marginTop, dimensions.height + dimensions.marginTop];
  }
  if (position === "right") {
    return [
      dimensions.width - dimensions.marginRight - dimensions.padding,
      dimensions.marginLeft + dimensions.padding,
    ];
  }
  if (position === "top") {
    return [
      dimensions.width + dimensions.marginLeft + dimensions.padding,
      dimensions.marginLeft + dimensions.padding,
    ];
  }
  if (position === "bottom") {
    return [
      dimensions.width + dimensions.marginLeft,
      dimensions.marginLeft + dimensions.padding,
    ];
  }
  return [0, 0];
};

export function createOrUpdateScales(
  scales: ScaleConfig[],
  _dimensions: Dimensions,
) {
  const dimensions = {
    ..._dimensions,
    height: _dimensions.height,
  };

  const scalesWithScales = scales?.map((scale: ScaleConfig) => {
    if (scale.type === "categorical") {
      return {
        ...scale,
        scale: d3
          .scaleBand()
          .domain((scale?.domain as string[]) ?? [])
          .range(positionRange(scale.position, dimensions))
          .padding(0.08)
          .paddingInner(scale.extraSpace || 0.1),
      };
    }
    if (scale.type === "total" || scale.type === "continuous") {
      const originalDomain = scale.domain;
      if (!originalDomain) return null;
      const d3Scale = d3
        .scaleLinear()
        .domain(originalDomain as number[])
        .range(positionRange(scale.position, dimensions))
        .nice();

      if (scale.zeroAsMinValue !== undefined) {
        const niceDomain = d3Scale.domain();
        const minValue = scale.zeroAsMinValue
          ? 0
          : Math.min(originalDomain[0] as number, originalDomain[1] as number);

        if (niceDomain[0] > niceDomain[1]) {
          d3Scale.domain([niceDomain[0], minValue]);
        } else {
          d3Scale.domain([minValue, niceDomain[1]]);
        }
      }

      return {
        ...scale,
        scale: d3Scale,
      };
    }

    if (scale.type === "time") {
      return {
        ...scale,
        scale: d3
          .scaleTime()
          .domain(
            (scale.domain as (string | number)[]).map(
              (d: string | number) => new Date(d),
            ),
          )
          .range(positionRange(scale.position, dimensions))
          .nice(),
      };
    }

    if (scale.type === "fill") {
      const stackScale = d3
        .scaleOrdinal()
        .domain((scale?.domain as string[]) ?? [])
        .range((scale?.range as string[]) ?? [])
        .unknown("#ddd");

      const strokeScale = d3
        .scaleOrdinal()
        .domain((scale.domain as string[]) ?? [])
        .range((scale.strokeRange as string[]) ?? [])
        .unknown("#ddd");
      return {
        ...scale,
        scale: stackScale,
        strokeScale: strokeScale,
      };
    }

    if (scale.type === "subgroup") return null;

    return scale;
  });

  if (
    scales?.find(
      (scale: ScaleConfig) =>
        scale?.type === "subgroup" && scale?.dataMapping !== "none",
    )
  ) {
    const subgroupScale = scales?.find(
      (scale: ScaleConfig) =>
        scale?.type === "subgroup" && scale?.dataMapping !== "none",
    );
    const categoricalScale = scalesWithScales.find(
      (scale: ScaleConfig | null) => scale?.type === "categorical",
    );

    scalesWithScales.push({
      ...subgroupScale,
      scale: d3
        .scaleBand()
        .domain((subgroupScale?.domain as string[]) ?? [])
        .range([
          0,
          (categoricalScale?.scale as d3.ScaleBand<string>).bandwidth(),
        ])
        .padding(0.08),
      scaleFunction: (dimensions: Dimensions) =>
        createOrUpdateScales(scales, dimensions),
    } as ScaleConfig);
  }

  return scalesWithScales?.filter((scale) => scale);
}
